import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useId } from "react";

export type DSInputSize = "sm" | "md";

export interface DSInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "className"
> {
  size?: DSInputSize;
  label?: string;
  /** Optional leading icon */
  leadingIcon?: IconProp;
  error?: string;
  dataTestId: string;
  ref?: React.Ref<HTMLInputElement>;
}

export const DSInput = ({
  size = "sm",
  label,
  leadingIcon,
  error,
  disabled = false,
  dataTestId,
  id: providedId,
  ref,
  ...props
}: DSInputProps) => {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  // Container styles
  const containerStyles = "flex flex-col gap-xs w-full";

  // Wrapper styles (for the input + icon container)
  const wrapperStyles = "relative flex items-center";

  // Base input styles
  const baseInputStyles =
    "w-full rounded-standard bg-input text-primary font-body text-sm transition-all placeholder:text-placeholder";

  // Size styles
  const sizeStyles = {
    sm: leadingIcon ? "h-10 pl-10 pr-md py-3" : "h-10 px-md py-3",
    md: leadingIcon
      ? "h-12 pl-10 pr-md py-3 text-base"
      : "h-12 px-md py-3 text-base",
  };

  // State styles
  const stateStyles = disabled
    ? "opacity-80 cursor-not-allowed shadow-[0px_0px_10px_0px_var(--shadow-secondary)] text-placeholder border-2 border-input"
    : error
      ? "shadow-[0px_0px_10px_0px_var(--shadow-primary)] border-2 border-danger focus:shadow-[0px_0px_10px_0px_var(--shadow-focus)] focus:border-danger focus:outline-none"
      : "shadow-[0px_0px_10px_0px_var(--shadow-primary)] border-2 border-input focus:shadow-[0px_0px_10px_0px_var(--shadow-focus)] focus:border-brand focus:outline-none hover:shadow-[0px_0px_10px_0px_var(--shadow-focus)]";

  // Icon styles
  const iconStyles =
    "absolute left-md pointer-events-none text-brand text-base";

  const disabledIconStyles =
    "absolute left-md pointer-events-none text-placeholder text-base";

  return (
    <div className={containerStyles}>
      {label && (
        <label
          htmlFor={id}
          className="text-xs text-secondary font-body"
        >
          {label}
        </label>
      )}
      <div className={wrapperStyles}>
        {leadingIcon && (
          <FontAwesomeIcon
            icon={leadingIcon}
            className={disabled ? disabledIconStyles : iconStyles}
            aria-hidden="true"
          />
        )}
        <input
          ref={ref}
          id={id}
          disabled={disabled}
          data-testid={dataTestId}
          data-intercom-target={dataTestId}
          className={`${baseInputStyles} ${sizeStyles[size]} ${stateStyles}`}
          {...props}
        />
      </div>
      {error && !disabled && (
        <span className="text-xs text-danger">{error}</span>
      )}
    </div>
  );
};
